/**
 * Sekonic L-858D の表示値 ⇔ EV（ISO 100）の換算
 *
 * メーターは F 優先・反射 1° で使う。設定した ISO と絞りで、メーターが表示した
 * シャッター速度（と 1/10 段表示）を入力する。計算は ⅓ 段の目盛り番号で行う。
 *
 *   AVi  = 絞りの目盛り番号（f/1 = 0、⅓ 段ごとに +1。f/2.8 = 9、f/8 = 18、f/11 = 21）
 *   TVi  = シャッター速度の目盛り番号（1s = 0、⅓ 段速くなるごとに +1）
 *   ISOi = ISO の目盛り番号（ISO 100 = 0、⅓ 段ごとに ±1）
 *   d    = 1/10 段表示の数字。「30₅」は 1/30 より 0.5 段速い側（EV +0.5）
 *
 *   EV(ISO 100) = (AVi + TVi − ISOi) / 3 + d / 10
 *
 * シャッター速度は SSL の添字 i で持つ（i = 0 が 1/8000、i = 39 が 1s）。TVi = 39 − i。
 */

export const SSL = ['1/8000', '1/6400', '1/5000', '1/4000', '1/3200', '1/2500', '1/2000', '1/1600', '1/1250', '1/1000', '1/800', '1/640', '1/500', '1/400', '1/320', '1/250', '1/200', '1/160', '1/125', '1/100', '1/80', '1/60', '1/50', '1/40', '1/30', '1/25', '1/20', '1/15', '1/13', '1/10', '1/8', '1/6', '1/5', '1/4', '0.3', '0.4', '0.5', '0.6', '0.8', '1', '1.3', '1.6', '2', '2.5', '3.2', '4', '5', '6', '8', '10', '13', '15', '20', '25', '30', '40', '50', '60'];
export const SS_MAX = SSL.length - 1; // 57

/** 絞りの目盛り番号 → 表示（f/2.8〜f/45） */
export const APL = { 9: '2.8', 10: '3.2', 11: '3.5', 12: '4', 13: '4.5', 14: '5', 15: '5.6', 16: '6.3', 17: '7.1', 18: '8', 19: '9', 20: '10', 21: '11', 22: '13', 23: '14', 24: '16', 25: '18', 26: '20', 27: '22', 28: '25', 29: '29', 30: '32', 31: '36', 32: '40', 33: '45' };
export const AP_MIN = 9;
export const AP_MAX = 33;

/** ISO（⅓ 段の公称値）。添字 − 12 が ISOi（ISO 100 = 0） */
export const ISOS = [6, 8, 10, 12, 16, 20, 25, 32, 40, 50, 64, 80, 100, 125, 160, 200, 250, 320, 400, 500, 640, 800, 1000, 1250, 1600, 2000, 2500, 3200, 4000, 5000, 6400];
const ISO_BASE = 12;

export function isoIndexOf(iso) {
  const n = Number(iso);
  const exact = ISOS.indexOf(n);
  if (exact >= 0) return exact - ISO_BASE;
  if (!Number.isFinite(n) || n <= 0) return 0;
  const k = Math.round(3 * Math.log2(n / 100));
  return Math.max(-ISO_BASE, Math.min(ISOS.length - 1 - ISO_BASE, k));
}
export function isoLabel(k) {
  return String(ISOS[Math.max(0, Math.min(ISOS.length - 1, k + ISO_BASE))]);
}
export const ISO_K_MIN = -ISO_BASE;
export const ISO_K_MAX = ISOS.length - 1 - ISO_BASE;

/** 絞り値（'11' など）→ 目盛り番号 */
export function apertureIndexOf(ap) {
  const s = String(ap);
  for (const [k, v] of Object.entries(APL)) if (v === s) return Number(k);
  const n = parseFloat(s);
  if (!Number.isFinite(n) || n <= 0) return 21;
  return Math.max(AP_MIN, Math.min(AP_MAX, Math.round(6 * Math.log2(n))));
}

/** メーターの段：'1' → 目盛り 3 つ分、'1/3' → 1 つ分 */
export function stepSize(sekStep) { return sekStep === '1/3' ? 1 : 3; }
/** 1/10 段表示の上限（1 段なら 0〜9、⅓ 段なら 0〜2） */
export function tenthMax(sekStep) { return sekStep === '1/3' ? 2 : 9; }

/** EV → 表示値 { ib: シャッター速度の添字, t: 1/10 段の数字 } */
export function splitEv(ev, { ap, isoK, sekStep, tenth }) {
  const S = stepSize(sekStep);
  const ic = ap + 39 - isoK - 3 * ev; // 連続値の添字
  let ib;
  let t = 0;
  if (tenth) {
    ib = S * Math.ceil(ic / S - 1e-6);
    t = Math.round(((ib - ic) / 3) * 10);
    if (t > tenthMax(sekStep)) { ib -= S; t = 0; }
  } else {
    ib = S * Math.round(ic / S);
  }
  if (ib > SS_MAX) { ib = SS_MAX - (SS_MAX % S); t = 0; }
  if (ib < 0) { ib = 0; t = 0; }
  return { ib, t };
}

/** 表示値 → EV（ISO 100） */
export function evFrom(ib, t, { ap, isoK }) {
  return (ap + 39 - isoK - (ib - t * 0.3)) / 3;
}

/** ± ボタン 1 回分。1/10 段表示なら 0.1 段ずつ（繰り上がりあり） */
export function bumpSek(ev, dir, opts) {
  const S = stepSize(opts.sekStep);
  const tm = tenthMax(opts.sekStep);
  let { ib, t } = splitEv(ev, opts);
  if (opts.tenth) {
    t += dir;
    if (t > tm) { t = 0; ib -= S; }
    if (t < 0) { t = tm; ib += S; }
  } else {
    ib -= dir * S;
  }
  if (ib < 0) { ib = 0; t = 0; }
  if (ib > SS_MAX) { ib = SS_MAX; t = 0; }
  return evFrom(ib, t, opts);
}

export function shutterText(ib) {
  const v = SSL[ib];
  return v.includes('/') ? v : `${v}″`;
}

/** 露出結果を L-858D の表示（F 優先）に戻したときのシャッター速度 */
export function shutterForEv(evIso100, { ap, isoK }) {
  const i = Math.max(0, Math.min(SS_MAX, ap + 39 - isoK - Math.round(evIso100 * 3)));
  return shutterText(i);
}

/** 換算の行：「= 1/30 +0.5 段 · EV 6.5」 */
export function conversionLine(ev, opts) {
  const { ib, t } = splitEv(ev, opts);
  return `= ${SSL[ib]}${t ? ` +0.${t} 段` : ''} · EV ${(Math.round(ev * 10) / 10).toFixed(1)}`;
}
